// ============================================
// Biomarker Correlation Engine
// ============================================
//
// Applies correlation rules to raw biomarker values
// to produce clinically plausible synthetic panels.
// When a primary biomarker deviates from normal,
// correlated biomarkers shift proportionally.
// ============================================

import { BIOMARKER_CORRELATIONS } from '../../data/biomarkers/biomarkerCorrelations';
import type { BiomarkerCorrelation } from '../../types/patientJourney';

// Reference ranges for adjustment calculations (conventional midpoints)
const REFERENCE_RANGES: Record<string, { low: number; high: number; mid: number }> = {
  'TSH': { low: 0.4, high: 4.5, mid: 2.45 },
  'Free T4': { low: 0.8, high: 1.8, mid: 1.3 },
  'Free T3': { low: 2.0, high: 4.4, mid: 3.2 },
  'Estradiol': { low: 12.5, high: 166, mid: 75 },
  'FSH': { low: 3.5, high: 12.5, mid: 7.0 },
  'Fasting Insulin': { low: 2.6, high: 24.9, mid: 5.0 },
  'Fasting Glucose': { low: 65, high: 99, mid: 82 },
  'Hemoglobin A1c': { low: 4.0, high: 5.6, mid: 5.0 },
  'Vitamin D (25-OH)': { low: 30, high: 100, mid: 55 },
  'Calcium': { low: 8.5, high: 10.5, mid: 9.5 },
  'hs-CRP': { low: 0, high: 3.0, mid: 0.8 },
  'Ferritin': { low: 12, high: 150, mid: 70 },
  'Testosterone Total': { low: 264, high: 916, mid: 550 },
  'SHBG': { low: 10, high: 57, mid: 30 },
  'Iron': { low: 60, high: 170, mid: 100 },
  'Hemoglobin': { low: 12.0, high: 17.5, mid: 14.5 },
  'RDW': { low: 11.5, high: 14.5, mid: 13.0 },
  'RBC': { low: 4.0, high: 5.5, mid: 4.5 },
  'BUN': { low: 6, high: 20, mid: 13 },
  'Creatinine': { low: 0.6, high: 1.2, mid: 0.9 },
  'eGFR': { low: 60, high: 120, mid: 95 },
  'ALT': { low: 7, high: 56, mid: 22 },
  'AST': { low: 10, high: 40, mid: 22 },
  'GGT': { low: 0, high: 65, mid: 18 },
  'Total Cholesterol': { low: 125, high: 200, mid: 175 },
  'LDL': { low: 0, high: 130, mid: 100 },
  'HDL': { low: 40, high: 100, mid: 60 },
  'Triglycerides': { low: 0, high: 150, mid: 75 },
  'LDL Particle Number': { low: 0, high: 1300, mid: 900 },
  'Progesterone': { low: 1.8, high: 24, mid: 12 },
  'LH': { low: 1.7, high: 8.6, mid: 5.0 },
  'Cortisol AM': { low: 6.2, high: 19.4, mid: 12 },
  'DHEA-S': { low: 35, high: 430, mid: 200 },
  'B12': { low: 200, high: 900, mid: 550 },
  'Folate': { low: 2.7, high: 17.0, mid: 10 },
  'Homocysteine': { low: 0, high: 15, mid: 7 },
  'PTH': { low: 15, high: 65, mid: 35 },
  'Albumin': { low: 3.5, high: 5.5, mid: 4.3 },
  'Total Protein': { low: 6.0, high: 8.3, mid: 7.0 },
  'WBC': { low: 4.5, high: 11.0, mid: 6.5 },
  'Neutrophils': { low: 40, high: 70, mid: 55 },
  'HOMA-IR': { low: 0, high: 2.5, mid: 1.0 },
  'ESR': { low: 0, high: 20, mid: 8 },
  'Platelets': { low: 150, high: 400, mid: 250 },
};

const DAMPENING: Record<BiomarkerCorrelation['strength'], number> = {
  strong: 0.7,
  moderate: 0.5,
  weak: 0.3,
};

function getRange(biomarker: string): { low: number; high: number; mid: number } | undefined {
  return REFERENCE_RANGES[biomarker];
}

function deviationFraction(value: number, range: { low: number; high: number; mid: number }): number {
  if (value > range.high) {
    return (value - range.high) / (range.high - range.low);
  }
  if (value < range.low) {
    return (range.low - value) / (range.high - range.low);
  }
  return 0;
}

function deviationDirection(value: number, range: { low: number; high: number }): 'high' | 'low' | 'normal' {
  if (value > range.high) return 'high';
  if (value < range.low) return 'low';
  return 'normal';
}

function clampToRange(value: number, biomarker: string, allowOutOfRange: boolean): number {
  const range = getRange(biomarker);
  if (!range) return value;
  if (allowOutOfRange) {
    const floor = range.low * 0.3;
    const ceiling = range.high * 2.5;
    return Math.max(floor, Math.min(ceiling, value));
  }
  return value;
}

// --- Individual Adjustment Functions ---

function inverseTSH_FT4(primary: number, current: number, dampening: number): number {
  const tshRange = getRange('TSH')!;
  const ft4Range = getRange('Free T4')!;
  const dir = deviationDirection(primary, tshRange);
  const frac = deviationFraction(primary, tshRange);
  if (dir === 'high') {
    return current - frac * (ft4Range.high - ft4Range.low) * dampening;
  }
  if (dir === 'low') {
    return current + frac * (ft4Range.high - ft4Range.low) * dampening;
  }
  return current;
}

function inverseTSH_FT3(primary: number, current: number, dampening: number): number {
  const tshRange = getRange('TSH')!;
  const ft3Range = getRange('Free T3')!;
  const dir = deviationDirection(primary, tshRange);
  const frac = deviationFraction(primary, tshRange);
  if (dir === 'high') {
    return current - frac * (ft3Range.high - ft3Range.low) * dampening * 0.8;
  }
  if (dir === 'low') {
    return current + frac * (ft3Range.high - ft3Range.low) * dampening * 0.8;
  }
  return current;
}

function inverseEstradiol_FSH(primary: number, current: number, dampening: number): number {
  const e2Range = getRange('Estradiol')!;
  const fshRange = getRange('FSH')!;
  const dir = deviationDirection(primary, e2Range);
  const frac = deviationFraction(primary, e2Range);
  if (dir === 'low') {
    return current + frac * (fshRange.high - fshRange.low) * dampening;
  }
  if (dir === 'high') {
    return current - frac * (fshRange.high - fshRange.low) * dampening * 0.4;
  }
  return current;
}

function insulinResistancePattern(primary: number, current: number, dampening: number): number {
  const insulinRange = getRange('Fasting Insulin')!;
  const glucoseRange = getRange('Fasting Glucose')!;
  const dir = deviationDirection(primary, insulinRange);
  const frac = deviationFraction(primary, insulinRange);
  if (dir === 'high') {
    return current + frac * (glucoseRange.high - glucoseRange.low) * dampening * 0.6;
  }
  return current;
}

function conditionalInsulin_HbA1c(primary: number, current: number, dampening: number): number {
  const insulinRange = getRange('Fasting Insulin')!;
  const a1cRange = getRange('Hemoglobin A1c')!;
  const dir = deviationDirection(primary, insulinRange);
  const frac = deviationFraction(primary, insulinRange);
  if (dir === 'high' && frac > 0.5) {
    return current + frac * (a1cRange.high - a1cRange.low) * dampening * 0.4;
  }
  return current;
}

function positiveVitD_Calcium(primary: number, current: number, dampening: number): number {
  const vitDRange = getRange('Vitamin D (25-OH)')!;
  const caRange = getRange('Calcium')!;
  const dir = deviationDirection(primary, vitDRange);
  const frac = deviationFraction(primary, vitDRange);
  if (dir === 'low') {
    return current - frac * (caRange.high - caRange.low) * dampening * 0.5;
  }
  return current;
}

function inflammationFerritin(primary: number, current: number, dampening: number): number {
  const crpRange = getRange('hs-CRP')!;
  const ferritinRange = getRange('Ferritin')!;
  const dir = deviationDirection(primary, crpRange);
  const frac = deviationFraction(primary, crpRange);
  if (dir === 'high') {
    return current + frac * (ferritinRange.high - ferritinRange.low) * dampening;
  }
  return current;
}

function conditionalTestosterone_SHBG(primary: number, current: number, dampening: number): number {
  const tRange = getRange('Testosterone Total')!;
  const shbgRange = getRange('SHBG')!;
  const dir = deviationDirection(primary, tRange);
  const frac = deviationFraction(primary, tRange);
  if (dir === 'low') {
    return current - frac * (shbgRange.high - shbgRange.low) * dampening * 0.5;
  }
  if (dir === 'high') {
    return current + frac * (shbgRange.high - shbgRange.low) * dampening * 0.3;
  }
  return current;
}

function positiveIron_Hemoglobin(primary: number, current: number, dampening: number): number {
  const ironRange = getRange('Iron')!;
  const hgbRange = getRange('Hemoglobin')!;
  const dir = deviationDirection(primary, ironRange);
  const frac = deviationFraction(primary, ironRange);
  if (dir === 'low') {
    return current - frac * (hgbRange.high - hgbRange.low) * dampening;
  }
  return current;
}

function inverseIron_RDW(primary: number, current: number, dampening: number): number {
  const ironRange = getRange('Iron')!;
  const rdwRange = getRange('RDW')!;
  const dir = deviationDirection(primary, ironRange);
  const frac = deviationFraction(primary, ironRange);
  if (dir === 'low') {
    return current + frac * (rdwRange.high - rdwRange.low) * dampening;
  }
  return current;
}

function positiveFerritin_Iron(primary: number, current: number, dampening: number): number {
  const ferritinRange = getRange('Ferritin')!;
  const ironRange = getRange('Iron')!;
  const dir = deviationDirection(primary, ferritinRange);
  const frac = deviationFraction(primary, ferritinRange);
  if (dir === 'low') {
    return current - frac * (ironRange.high - ironRange.low) * dampening;
  }
  return current;
}

function positiveBUN_Creatinine(primary: number, current: number, dampening: number): number {
  const bunRange = getRange('BUN')!;
  const crRange = getRange('Creatinine')!;
  const dir = deviationDirection(primary, bunRange);
  const frac = deviationFraction(primary, bunRange);
  if (dir === 'high') {
    return current + frac * (crRange.high - crRange.low) * dampening;
  }
  return current;
}

function positiveALT_AST(primary: number, current: number, dampening: number): number {
  const altRange = getRange('ALT')!;
  const astRange = getRange('AST')!;
  const dir = deviationDirection(primary, altRange);
  const frac = deviationFraction(primary, altRange);
  if (dir === 'high') {
    return current + frac * (astRange.high - astRange.low) * dampening * 0.85;
  }
  return current;
}

function positiveALT_GGT(primary: number, current: number, dampening: number): number {
  const altRange = getRange('ALT')!;
  const ggtRange = getRange('GGT')!;
  const dir = deviationDirection(primary, altRange);
  const frac = deviationFraction(primary, altRange);
  if (dir === 'high') {
    return current + frac * (ggtRange.high - ggtRange.low) * dampening * 0.6;
  }
  return current;
}

function positiveTotalChol_LDL(primary: number, current: number, dampening: number): number {
  const tcRange = getRange('Total Cholesterol')!;
  const ldlRange = getRange('LDL')!;
  const dir = deviationDirection(primary, tcRange);
  const frac = deviationFraction(primary, tcRange);
  if (dir === 'high') {
    return current + frac * (ldlRange.high - ldlRange.low) * dampening;
  }
  if (dir === 'low') {
    return current - frac * (ldlRange.high - ldlRange.low) * dampening * 0.7;
  }
  return current;
}

function inverseHDL_Triglycerides(primary: number, current: number, dampening: number): number {
  const hdlRange = getRange('HDL')!;
  const tgRange = getRange('Triglycerides')!;
  const dir = deviationDirection(primary, hdlRange);
  const frac = deviationFraction(primary, hdlRange);
  if (dir === 'low') {
    return current + frac * (tgRange.high - tgRange.low) * dampening;
  }
  if (dir === 'high') {
    return current - frac * (tgRange.high - tgRange.low) * dampening * 0.4;
  }
  return current;
}

function positiveGlucose_HbA1c(primary: number, current: number, dampening: number): number {
  const gluRange = getRange('Fasting Glucose')!;
  const a1cRange = getRange('Hemoglobin A1c')!;
  const dir = deviationDirection(primary, gluRange);
  const frac = deviationFraction(primary, gluRange);
  if (dir === 'high') {
    return current + frac * (a1cRange.high - a1cRange.low) * dampening;
  }
  return current;
}

function conditionalEstradiol_Progesterone(primary: number, current: number, dampening: number): number {
  const e2Range = getRange('Estradiol')!;
  const progRange = getRange('Progesterone')!;
  const dir = deviationDirection(primary, e2Range);
  const frac = deviationFraction(primary, e2Range);
  if (dir === 'low') {
    return current - frac * (progRange.high - progRange.low) * dampening * 0.6;
  }
  return current;
}

function inverseTestosterone_LH(primary: number, current: number, dampening: number): number {
  const tRange = getRange('Testosterone Total')!;
  const lhRange = getRange('LH')!;
  const dir = deviationDirection(primary, tRange);
  const frac = deviationFraction(primary, tRange);
  if (dir === 'low') {
    return current + frac * (lhRange.high - lhRange.low) * dampening;
  }
  if (dir === 'high') {
    return current - frac * (lhRange.high - lhRange.low) * dampening * 0.4;
  }
  return current;
}

function inverseCortisol_DHEAS(primary: number, current: number, dampening: number): number {
  const cortRange = getRange('Cortisol AM')!;
  const dheasRange = getRange('DHEA-S')!;
  const dir = deviationDirection(primary, cortRange);
  const frac = deviationFraction(primary, cortRange);
  if (dir === 'high') {
    return current - frac * (dheasRange.high - dheasRange.low) * dampening * 0.5;
  }
  return current;
}

function inverseB12_Homocysteine(primary: number, current: number, dampening: number): number {
  const b12Range = getRange('B12')!;
  const hcyRange = getRange('Homocysteine')!;
  const dir = deviationDirection(primary, b12Range);
  const frac = deviationFraction(primary, b12Range);
  if (dir === 'low') {
    return current + frac * (hcyRange.high - hcyRange.low) * dampening;
  }
  return current;
}

function inverseFolate_Homocysteine(primary: number, current: number, dampening: number): number {
  const folRange = getRange('Folate')!;
  const hcyRange = getRange('Homocysteine')!;
  const dir = deviationDirection(primary, folRange);
  const frac = deviationFraction(primary, folRange);
  if (dir === 'low') {
    return current + frac * (hcyRange.high - hcyRange.low) * dampening;
  }
  return current;
}

function inverseVitD_PTH(primary: number, current: number, dampening: number): number {
  const vitDRange = getRange('Vitamin D (25-OH)')!;
  const pthRange = getRange('PTH')!;
  const dir = deviationDirection(primary, vitDRange);
  const frac = deviationFraction(primary, vitDRange);
  if (dir === 'low') {
    return current + frac * (pthRange.high - pthRange.low) * dampening;
  }
  if (dir === 'high') {
    return current - frac * (pthRange.high - pthRange.low) * dampening * 0.3;
  }
  return current;
}

function positiveAlbumin_TotalProtein(primary: number, current: number, dampening: number): number {
  const albRange = getRange('Albumin')!;
  const tpRange = getRange('Total Protein')!;
  const dir = deviationDirection(primary, albRange);
  const frac = deviationFraction(primary, albRange);
  if (dir === 'low') {
    return current - frac * (tpRange.high - tpRange.low) * dampening * 0.6;
  }
  return current;
}

function positiveWBC_Neutrophils(primary: number, current: number, dampening: number): number {
  const wbcRange = getRange('WBC')!;
  const neutRange = getRange('Neutrophils')!;
  const dir = deviationDirection(primary, wbcRange);
  const frac = deviationFraction(primary, wbcRange);
  if (dir === 'high') {
    return current + frac * (neutRange.high - neutRange.low) * dampening;
  }
  if (dir === 'low') {
    return current - frac * (neutRange.high - neutRange.low) * dampening * 0.7;
  }
  return current;
}

function positiveTG_LDLP(primary: number, current: number, dampening: number): number {
  const tgRange = getRange('Triglycerides')!;
  const ldlpRange = getRange('LDL Particle Number')!;
  const dir = deviationDirection(primary, tgRange);
  const frac = deviationFraction(primary, tgRange);
  if (dir === 'high') {
    return current + frac * (ldlpRange.high - ldlpRange.low) * dampening * 0.7;
  }
  return current;
}

function positiveHemoglobin_RBC(primary: number, current: number, dampening: number): number {
  const hgbRange = getRange('Hemoglobin')!;
  const rbcRange = getRange('RBC')!;
  const dir = deviationDirection(primary, hgbRange);
  const frac = deviationFraction(primary, hgbRange);
  if (dir === 'low') {
    return current - frac * (rbcRange.high - rbcRange.low) * dampening;
  }
  if (dir === 'high') {
    return current + frac * (rbcRange.high - rbcRange.low) * dampening * 0.8;
  }
  return current;
}

function positiveInsulin_HOMAIR(primary: number, current: number, dampening: number): number {
  const insulinRange = getRange('Fasting Insulin')!;
  const homaRange = getRange('HOMA-IR')!;
  const dir = deviationDirection(primary, insulinRange);
  const frac = deviationFraction(primary, insulinRange);
  if (dir === 'high') {
    return current + frac * (homaRange.high - homaRange.low) * dampening;
  }
  return current;
}

function inverseCreatinine_eGFR(primary: number, current: number, dampening: number): number {
  const crRange = getRange('Creatinine')!;
  const egfrRange = getRange('eGFR')!;
  const dir = deviationDirection(primary, crRange);
  const frac = deviationFraction(primary, crRange);
  if (dir === 'high') {
    return current - frac * (egfrRange.high - egfrRange.low) * dampening;
  }
  return current;
}

function positiveHsCRP_ESR(primary: number, current: number, dampening: number): number {
  const crpRange = getRange('hs-CRP')!;
  const esrRange = getRange('ESR')!;
  const dir = deviationDirection(primary, crpRange);
  const frac = deviationFraction(primary, crpRange);
  if (dir === 'high') {
    return current + frac * (esrRange.high - esrRange.low) * dampening;
  }
  return current;
}

// Adjustment function registry
const ADJUSTMENT_FNS: Record<string, (primary: number, current: number, dampening: number) => number> = {
  inverseTSH_FT4,
  inverseTSH_FT3,
  inverseEstradiol_FSH,
  insulinResistancePattern,
  conditionalInsulin_HbA1c,
  positiveVitD_Calcium,
  inflammationFerritin,
  conditionalTestosterone_SHBG,
  positiveIron_Hemoglobin,
  inverseIron_RDW,
  positiveFerritin_Iron,
  positiveBUN_Creatinine,
  positiveALT_AST,
  positiveALT_GGT,
  positiveTotalChol_LDL,
  inverseHDL_Triglycerides,
  positiveGlucose_HbA1c,
  conditionalEstradiol_Progesterone,
  inverseTestosterone_LH,
  inverseCortisol_DHEAS,
  inverseB12_Homocysteine,
  inverseFolate_Homocysteine,
  inverseVitD_PTH,
  positiveAlbumin_TotalProtein,
  positiveWBC_Neutrophils,
  positiveTG_LDLP,
  positiveHemoglobin_RBC,
  positiveInsulin_HOMAIR,
  inverseCreatinine_eGFR,
  positiveHsCRP_ESR,
};

export function applyCorrelations(
  rawValues: Map<string, number>,
  correlations: BiomarkerCorrelation[] = BIOMARKER_CORRELATIONS,
): { adjustedValues: Map<string, number>; correlationsApplied: string[] } {
  const adjustedValues = new Map(rawValues);
  const correlationsApplied: string[] = [];

  for (const rule of correlations) {
    const primaryValue = adjustedValues.get(rule.primaryBiomarker);
    if (primaryValue === undefined) continue;

    const correlatedValue = adjustedValues.get(rule.correlatedBiomarker);
    if (correlatedValue === undefined) continue;

    const primaryRange = getRange(rule.primaryBiomarker);
    if (!primaryRange) continue;

    const dir = deviationDirection(primaryValue, primaryRange);
    if (dir === 'normal') continue;

    const adjustFn = ADJUSTMENT_FNS[rule.adjustmentFn];
    if (!adjustFn) continue;

    const dampeningFactor = DAMPENING[rule.strength];
    const newValue = adjustFn(primaryValue, correlatedValue, dampeningFactor);
    const clamped = clampToRange(newValue, rule.correlatedBiomarker, true);

    if (Math.abs(clamped - correlatedValue) > 0.001) {
      adjustedValues.set(rule.correlatedBiomarker, Math.round(clamped * 100) / 100);
      correlationsApplied.push(
        `${rule.primaryBiomarker} (${dir}) -> ${rule.correlatedBiomarker}: ${rule.rule}`,
      );
    }
  }

  return { adjustedValues, correlationsApplied };
}

export {
  REFERENCE_RANGES,
  ADJUSTMENT_FNS,
  DAMPENING,
};
